import { Callback1, Destructor, Type, VoidCallback1 } from '@deqo/foundation';
import produce from 'immer';
import { injectable } from 'inversify';


export type StoreObserver<T> = (newState: T, prevState: T) => void;

export interface Store<T> {

    getState(): T;

    observe(observer: StoreObserver<T>): Destructor;
    observe<P>(path: Callback1<P, T>, observer: StoreObserver<P>): Destructor;
}

export type StoreState<T> = T extends Store<infer U> ? U : never;
export type StoreProps<T> = keyof StoreState<T>;

@injectable()
export abstract class AbstractStore<T> implements Store<T> {

    protected observers = [] as StoreObserver<T>[];
    protected abstract state: T;

    public getState(): T {
        return this.state;
    }

    public observe(observer: StoreObserver<T>): Destructor;
    public observe<P>(path: Callback1<P, T>, observer: StoreObserver<P>): Destructor;

    public observe(a: any, b?: any): Destructor {
        if (b) return this.observe(pathObserver(a, b));
        this.observers.push(a);
        return () => this.unobserve(a);
    }

    protected unobserve(observer: StoreObserver<T>) {
        const index = this.observers.indexOf(observer);
        if (index >= 0) {
            this.observers.splice(index, 1);
        }
    }

    public mutate(mutator: VoidCallback1<T>): T;
    public mutate(update: Partial<T>): T;
    public mutate(input: any) {
        if (typeof (input) !== 'function') {
            return this.mutate(next => Object.assign(next, input));
        }
        return this.setStateAndNotify(
            produce(this.state, draft => void (input(draft as any)))
        );
    }

    public mutateTo(newState: T) {
        return this.setStateAndNotify(
            produce(this.state, () => newState) as any
        );
    }

    private setStateAndNotify(newState: T) {
        const oldState = this.state;
        this.state = newState;
        if ('debugName' in this) {
            // logStateDiff((this as any).debugName, newState, oldState);
        }
        this.observers.forEach(x => x(newState, oldState));
        return oldState;
    }
}

export const pathObserver = <T, P>(path: Callback1<P, T>, observer: StoreObserver<P>): StoreObserver<T> => {
    return (newState, oldState) => {
        const n = path(newState);
        const o = path(oldState);
        if (n !== o) {
            observer(n, o);
        }
    };
};

class SimpleStore<T> extends AbstractStore<T> {
    constructor(protected state: T) {
        super();
    }
    public update(data: Partial<T>): void {
        this.mutate(next => Object.assign(next, data));
    }
}

export type StoreWithUpdate<T> = Store<T> & {
    update(data: Partial<T>): void,
};

export const Store = {
    create<S, A = Record<string, unknown>>(initialState: S, attributes?: A): Readonly<S> & Readonly<A> & StoreWithUpdate<S> {

        const store = new SimpleStore<S>(initialState);

        for (const p in initialState) {
            if (p in store) continue;
            Object.defineProperty(store, p, {
                configurable: false,
                enumerable: true,
                get(this: Store<S>) {
                    return this.getState()[p];
                }
            });
        }

        for (const p in attributes) {
            if (p in store) continue;
            Object.defineProperty(store, p, {
                configurable: false,
                enumerable: true,
                value: attributes[p],
                writable: false,
            });
        }

        return store as any;
    },
    define<S, A = Record<string, unknown>>(initialState: S, attributes?: A): Type<Readonly<S> & Readonly<A> & StoreWithUpdate<S>> {

        const store = new SimpleStore<S>(initialState);

        for (const p in initialState) {
            if (p in store) continue;
            Object.defineProperty(store, p, {
                configurable: false,
                enumerable: true,
                get(this: Store<S>) {
                    return this.getState()[p];
                }
            });
        }

        for (const p in attributes) {
            if (p in store) continue;
            Object.defineProperty(store, p, {
                configurable: false,
                enumerable: true,
                value: attributes[p],
                writable: false,
            });
        }

        return store as any;
    },
    debug<T, S extends Store<T>>(name: string, store: S): S {
        // store.observe((ns, os) => logStateDiff(name, ns, os));
        return store;
    },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logStateDiff = (name: string, ns: any, os: any) => {
    const diff = [] as string[];
    for (const k of Object.keys(ns)) {
        if (ns[k] !== os[k]) {
            diff.push(k);
        }
    }
    // console.log(name, '~', diff.join(', '));
};
